import {
	ReactElement,
	MouseEvent,
	useMemo,
	useCallback,
	useState
} from "react";
import { execute as openDocumentLocation } from "client_legacy/Documents/SharePoint/Actions/openLocationAction";
import { DocumentItemViewModel, DocumentItemType } from "client/Api/Documents/SharePoint/common";
import { FileThumbnailKind, getExtensionAndType } from "client_legacy/Documents/fileExtensions";
import { never } from "client_legacy/ndaw/errors";
import {
	DateString,
	Icon,
	Space,
	Highlight,
	HighlightedText,
	PipeSeparator
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import {
	DocumentGlobalSearchResultItem,
	GlobalSearchScopeIdentifier,
	HighlightItem
} from "client/Api/TitleBar/GlobalSearch/globalSearch";
import {
	formatFileLocation,
	getLocationModelFromDocument,
	SharePointItemLocationViewModel
} from "../../Layout/TitleBar/GlobalSearch/Items/DocumentListItem";
import { useHighlights } from "../../Layout/TitleBar/GlobalSearch/ResultView/CategoryView/ItemView/useHighlights";
import { ItemLayout } from "./ItemLayout";
import { documentsConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import { createUrlRelativeToEnvironment } from "client_legacy/ndaw/navigator";
import { urlEncodeObject } from "client_legacy/ndaw/urlHelper";
import { convertToLocationModel } from "client_legacy/Documents/SharePoint/listLocationModelProvider";
import styles from "./DocumentItemView.module.less";
import {
	generateTrackingId,
	isNil,
	capitalizeFirstLetter
} from "client/Misc/utils";
import classNames from "classnames";
import { TimeZone } from "client/DateAndTime/timezones";
import { useDocumentNavigationHandler } from "client/Features/Layout/TitleBar/GlobalSearch/Navigation/useDocumentNavigationHandler";
import { SearchListItem } from "../SearchListItem";
import { useKeyboardListNavigationItem } from "client/Hooks";
import { Key } from "client/Misc/commonTypes";

export type DocumentItemViewProps = {
	item: DocumentGlobalSearchResultItem;
	content?: string;
	navigationIndex: number;
	index: number;
	itemScope: GlobalSearchScopeIdentifier;
	isMobile: boolean;
	timezone: TimeZone;
};

export const DocumentItemView = (props: DocumentItemViewProps): ReactElement => {
	const {
		item,
		content,
		navigationIndex,
		index,
		itemScope,
		isMobile,
		timezone
	} = props;

	const {
		document,
		hitHighlights
	} = item;

	const executeHandler: () => void = useDocumentNavigationHandler({
		document,
		searchResult: item,
		itemScope
	});

	return (
		<SearchListItem
			onExecute={executeHandler}
			trackingId={generateTrackingId("document-item", `|${index}`)}
		>
			<SharePointDocumentItem
				document={document}
				content={content}
				hitHighlights={hitHighlights}
				isMobile={isMobile}
				timezone={timezone}
				navigationIndex={navigationIndex}
				onExecute={executeHandler}
			/>
		</SearchListItem>
	);
};
DocumentItemView.displayName = "DocumentItemView";

type SharePointDocumentItemProps = {
	document: DocumentItemViewModel;
	content?: string;
	hitHighlights: readonly HighlightItem[];
	isMobile: boolean;
	timezone: TimeZone;
	navigationIndex: number;
	onExecute: () => void;
};

const SharePointDocumentItem = (
	props: SharePointDocumentItemProps
): ReactElement => {
	const {
		content,
		document,
		hitHighlights,
		isMobile,
		timezone,
		navigationIndex,
		onExecute
	} = props;

	const {
		name,
		modifiedOn,
		modifiedBy,
		itemType
	} = document;

	const highlights: Map<string, Highlight> = useHighlights(hitHighlights);

	const locationModel: SharePointItemLocationViewModel = useMemo(
		(): SharePointItemLocationViewModel => getLocationModelFromDocument(document),
		[document]
	);

	const location = useMemo(
		(): string | null => {
			const Path: string | null = formatFileLocation(locationModel);

			return Path
				? capitalizeFirstLetter(Path)
				: null;
		},
		[locationModel]
	);

	const locationClickHandler = useCallback(
		(event: MouseEvent): void => {
			event.preventDefault();
			event.stopPropagation();
			openDocumentLocation(document, false);
		},
		[document]
	);

	let type: FileThumbnailKind;
	switch (itemType) {
		case DocumentItemType.File:
			({ type } = getExtensionAndType(name, false));
			break;
		case DocumentItemType.OneNoteNotebook:
			type = "onenote";
			break;
		case DocumentItemType.Folder:
			type = "folder";
			break;
		default:
			never(itemType);
	}

	const { ref, onKeyDown } = useKeyboardListNavigationItem({
		itemIndex: navigationIndex,
		onExecute: onExecute,
		executeKey: [Key.Enter, Key.Space],
	});

	return (
		<ItemLayout
			ref={ref}
			onKeyDown={onKeyDown}
			tabIndex={-1}
		>
			<ItemLayout.IconContainer>
				<Icon
					value={{ kind: "svg", name: type }}
				/>
			</ItemLayout.IconContainer>

			<ItemLayout.InfoWrapper>
				<ItemLayout.Title
					trackingId="title"
					title={name}
				>
					<HighlightedText
						text={name}
						highlight={highlights.get("document.name")}
					/>
				</ItemLayout.Title>

				<ItemLayout.Info>
					<ItemLayout.InfoIconWrapper>
						<Icon value="pen" size="small" />
					</ItemLayout.InfoIconWrapper>

					<ItemLayout.SubTitle trackingId="modified-by">
						<DateString
							format="RelativeTimestamp"
							value={modifiedOn}
							timezone={timezone}
						/>
						<Space />
						{localize("_(Edited)By")}
						<Space />
						{modifiedBy}
					</ItemLayout.SubTitle>
					{locationModel.numberOfSegments > 0 && (
						<>
							<PipeSeparator />
							<ItemLayout.Link
								trackingId="location"
								onClick={locationClickHandler}
							>
								{location}
							</ItemLayout.Link>
						</>
					)}
				</ItemLayout.Info>

				{content && (
					<ItemLayout.Content>
						<HighlightedText
							text={content}
							highlight={highlights.get("content")}
						/>
					</ItemLayout.Content>
				)}
			</ItemLayout.InfoWrapper>

			<DocumentThumbnail
				document={document}
				isMobile={isMobile}
			/>

			<ItemLayout.LocationButton
				onClick={locationClickHandler}
			/>
		</ItemLayout>
	);
};

SharePointDocumentItem.displayName = "SharePointDocumentItem";

type DocumentThumbnailProps = {
	document: DocumentItemViewModel;
	isMobile: boolean;
};

const DocumentThumbnail = (props: DocumentThumbnailProps): ReactElement | null => {
	const {
		document,
		isMobile
	} = props;

	const [loadResult, setLoadingResult] = useState<boolean | undefined>();

	const { doesSupportThumbnails } = documentsConfiguration()
		.sharePoint;

	const shouldShowThumbnail: boolean = !isMobile
		&& doesSupportThumbnails
		&& document.itemType !== DocumentItemType.Folder;

	const thumbnailUrl = useMemo(
		(): string => {
			const queryString: string = urlEncodeObject({
				location: convertToLocationModel(document.location),
				itemId: document.id
			});

			return createUrlRelativeToEnvironment(`Documents/SharePoint/Thumbnail?${queryString}`);
		},
		[document.id, document.location]
	);

	if (!shouldShowThumbnail) {
		return null;
	}

	return (
		<ItemLayout.Thumbnail
			height="small"
			isHeightReserver={isNil(loadResult)}
		>
			{loadResult !== false && (
				<img
					className={classNames(
						styles.thumbnail,
						{ [styles.thumbnail_visible]: loadResult === true }
					)}
					src={thumbnailUrl}
					onLoad={() => setLoadingResult(true)}
					onError={() => setLoadingResult(false)}
				/>
			)}
		</ItemLayout.Thumbnail>
	);
};
DocumentThumbnail.displayName = "DocumentThumbnail";
